import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {NormaLinea} from '@/components/catalogo-kairos'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {PorQue} from '@/components/ui/por-que'
import {cn} from '@/lib/utils'
import type {EmparejamientoCargo, PerfilTipico} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'

const GRADO_TONE: Record<EmparejamientoCargo['grado'], 'green' | 'amber' | 'neutral'> = {
  seguro: 'green',
  probable: 'amber',
  sin_perfil: 'neutral',
}

/** Una sola traza por tarjeta: la pantalla lista todos los cargos y un disparador por dato
 * rompería el presupuesto de disparadores. */
function OrigenDelCargo({fila}: {fila: EmparejamientoCargo}) {
  const {t} = useTranslation()
  const marcas = fila.normalizacion.marcas.map((marca) => t(`perfilCargo.marca.${marca}`)).join(', ')
  const palabras = fila.normalizacion.palabras_descartadas.join(', ')
  const variosCandidatos = fila.candidatos.length > 1
  return (
    <PorQue
      label={t('common.deDondeSalio')}
      ariaLabel={`${t('common.deDondeSalio')} — ${fila.cargo}`}
      testId="descartes"
      contenidoTestId="descartes-contenido">
      {!marcas && !palabras ? (
        <p>{t('perfilCargo.sinDescartes')}</p>
      ) : (
        <p>
          {marcas && t('perfilCargo.descarto', {marcas})} {palabras && t('perfilCargo.descartoPalabras', {palabras})}
        </p>
      )}
      {fila.personas === 0 && <p data-testid="sin-titulares-contenido">{t('perfilCargo.sinTitularesPorqueLista')}</p>}
      {fila.candidatos.length === 0 && (
        <p data-testid="sin-perfil-tipico-contenido">{t('perfilCargo.sinPerfilTipicoPorque')}</p>
      )}
      {fila.candidatos.map((candidato) => (
        <div key={candidato.clave} className="space-y-1" data-testid={`empareja-contenido-${candidato.clave}`}>
          <p>
            {variosCandidatos && <span className="font-medium">{candidato.cargo_nombre}: </span>}
            {t('perfilCargo.compartidas', {palabras: candidato.palabras_compartidas.join(', ')})}
          </p>
          {candidato.palabras_compartidas.length === 1 && (
            <p data-testid="compartidas-debil">
              {t('perfilCargo.compartidasDebil', {palabra: candidato.palabras_compartidas[0]})}
            </p>
          )}
          {candidato.por_sinonimo && <p>{t('perfilCargo.porSinonimo')}</p>}
        </div>
      ))}
    </PorQue>
  )
}

function ListaBloque({etiqueta, lineas}: {etiqueta: string; lineas: string[]}) {
  if (lineas.length === 0) return null
  return (
    <div>
      <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">{etiqueta}</p>
      <ul className="mt-0.5 space-y-0.5 text-sm text-foreground">
        {lineas.map((linea) => (
          <li key={linea}>{linea}</li>
        ))}
      </ul>
    </div>
  )
}

function CuerpoPerfilTipico({perfil, testId}: {perfil: PerfilTipico; testId: string}) {
  const {t} = useTranslation()
  const [abierto, setAbierto] = useState(false)
  return (
    <>
      <Button
        className="mt-2 px-0"
        size="sm"
        variant="link"
        data-testid={`ver-${testId}`}
        onClick={() => setAbierto((previo) => !previo)}>
        {t(abierto ? 'perfilCargo.ocultarPerfilCompleto' : 'perfilCargo.verPerfilCompleto')}
      </Button>
      {abierto && (
        <div className="mt-2 space-y-2" data-testid={`cuerpo-${testId}`}>
          <ListaBloque etiqueta={t('perfilCargo.bloques.funciones')} lineas={perfil.funciones} />
          <ListaBloque etiqueta={t('perfilCargo.bloques.peligros')} lineas={perfil.peligros} />
          <ListaBloque etiqueta={t('perfilCargo.bloques.requerimientos')} lineas={perfil.requerimientos} />
          {perfil.condiciones.length > 0 && (
            <div>
              <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
                {t('perfilCargo.bloques.condiciones')}
              </p>
              <ul className="mt-0.5 space-y-1">
                {perfil.condiciones.map((fila) => (
                  <li key={fila.clave}>
                    <span className="text-sm text-foreground">{fila.nombre}</span>
                    <br />
                    <NormaLinea fila={fila} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          <PorQue
            label={t('perfilCargo.sinDatosClinicosLabel')}
            ariaLabel={`${t('perfilCargo.sinDatosClinicosLabel')} — ${perfil.cargo_nombre}`}
            testId={`sin-datos-clinicos-${testId}`}>
            <p>{t('perfilCargo.sinDatosClinicos')}</p>
          </PorQue>
        </div>
      )}
    </>
  )
}

/** Kairos no elige entre candidatos. El aplicado se marca para no invitar a aplicar el otro
 * «por si acaso». */
function CandidatoCard({
  candidato,
  ambiguo,
  elegido,
  puedeAplicar,
  pendiente,
  onUsar,
}: {
  candidato: PerfilTipico
  ambiguo: boolean
  elegido: boolean
  puedeAplicar: boolean
  pendiente: boolean
  onUsar: () => void
}) {
  const {t} = useTranslation()
  return (
    <article
      // Selección por región común (U12); «Elegido» es identidad, no estado: va en `neutral` (U2(a)).
      className={cn('surface-raised flex-1 rounded-lg p-3', elegido && 'ring-1 ring-foreground/40')}
      data-testid={`candidato-${candidato.clave}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="font-heading text-sm tracking-tight">{candidato.cargo_nombre}</h4>
        {candidato.por_sinonimo && <Tag tone="amber">{t('perfilCargo.grado.probable')}</Tag>}
        {elegido && <Tag tone="neutral">{t('perfilCargo.elegido')}</Tag>}
      </div>
      <CuerpoPerfilTipico perfil={candidato} testId={candidato.clave} />
      {puedeAplicar && (
        <div>
          <Button
            className="mt-2"
            size="sm"
            disabled={pendiente}
            onClick={onUsar}
            data-testid={`usar-${candidato.clave}`}
            variant={ambiguo || elegido ? 'outline' : 'default'}>
            {t(elegido ? 'perfilCargo.usarPerfilOtraVez' : 'perfilCargo.usarPerfil')}
          </Button>
        </div>
      )}
    </article>
  )
}

export function PropuestaCargo({
  fila,
  disponibles,
  puedeAplicar,
  pendiente,
  onUsar,
}: {
  fila: EmparejamientoCargo
  disponibles: PerfilTipico[]
  puedeAplicar: boolean
  pendiente: boolean
  onUsar: (accion: 'aceptar' | 'cambiar' | 'descartar', catalogoKey?: string) => void
}) {
  const {t} = useTranslation()
  const [confirmando, setConfirmando] = useState(false)
  const aplicado = fila.origen_catalogo_key
  const otros = disponibles.filter((perfil) => !fila.candidatos.some((c) => c.clave === perfil.clave))

  return (
    <article className="surface rounded-lg p-4" data-testid={`emparejamiento-${fila.cargo}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-sm tracking-tight">{fila.cargo}</h3>
        <Tag tone={GRADO_TONE[fila.grado]}>{t(`perfilCargo.grado.${fila.grado}`)}</Tag>
        {fila.personas === 0 ? (
          // Sin titulares es un hecho del dato, no un pendiente: `neutral` y no ámbar (U2(b)).
          <Tag tone="neutral">{t('perfilCargo.personas', {count: 0})}</Tag>
        ) : (
          <span className="text-xs text-muted-foreground">{t('perfilCargo.personas', {count: fila.personas})}</span>
        )}
      </div>
      <OrigenDelCargo fila={fila} />
      {aplicado && (
        <p className="notice-ok mt-2 rounded-lg px-3 py-2 text-sm" data-testid="rotulo-aplicado">
          {t('perfilCargo.aplicado', {perfil: fila.origen_catalogo_nombre})}
        </p>
      )}
      {fila.ambiguo && !aplicado && (
        <p className="notice-amber mt-2 rounded-lg px-3 py-2 text-sm" data-testid="ambiguo">
          {t('perfilCargo.ambiguo')}
        </p>
      )}
      {fila.candidatos.length === 0 && (
        <p className="mt-2 text-sm text-muted-foreground" data-testid={`sin-perfil-tipico-${fila.cargo}`}>
          {t('perfilCargo.sinPerfilTipico')}
        </p>
      )}
      <div className="mt-2 flex flex-col gap-3 sm:flex-row">
        {fila.candidatos.map((candidato) => (
          <CandidatoCard
            key={candidato.clave}
            candidato={candidato}
            ambiguo={fila.ambiguo}
            elegido={candidato.clave === aplicado}
            puedeAplicar={puedeAplicar}
            pendiente={pendiente}
            onUsar={() => onUsar(fila.ambiguo ? 'cambiar' : 'aceptar', candidato.clave)}
          />
        ))}
      </div>
      {puedeAplicar && otros.length > 0 && (
        <div className="mt-3 max-w-sm">
          <p className="text-xs text-muted-foreground">{t('perfilCargo.elegirOtro')}</p>
          <EntitySearchSelect<PerfilTipico>
            items={otros}
            value={null}
            getKey={(perfil) => perfil.clave}
            filterText={(perfil) => perfil.cargo_nombre}
            renderOption={(perfil) => perfil.cargo_nombre}
            renderTriggerValue={(perfil) => perfil.cargo_nombre}
            placeholder={t('perfilCargo.elegirOtro')}
            searchPlaceholder={t('perfilCargo.elegirOtroBuscar')}
            noResultsLabel={t('perfilCargo.elegirOtroVacio')}
            testId={`otro-perfil-${fila.cargo}`}
            disabled={pendiente}
            onPick={(perfil) => perfil && onUsar('cambiar', perfil.clave)}
          />
        </div>
      )}
      {puedeAplicar && (
        <div className="mt-3">
          {/* Con un perfil ya aplicado, descartar suelta las líneas del catálogo sin vuelta
              atrás: por eso ese camino pasa por el modal. */}
          <Button
            size="sm"
            variant="outline"
            disabled={pendiente}
            data-testid={`descartar-${fila.cargo}`}
            onClick={() => (aplicado ? setConfirmando(true) : onUsar('descartar'))}>
            {t('perfilCargo.descartar')}
          </Button>
          <div className="mt-1">
            <PorQue
              label={t('perfilCargo.descartarHintLabel')}
              ariaLabel={`${t('perfilCargo.descartarHintLabel')} — ${fila.cargo}`}
              testId={`descartar-hint-${fila.cargo}`}>
              <p>{t('perfilCargo.descartarHint')}</p>
            </PorQue>
          </div>
          <ConfirmDialog
            open={confirmando}
            onOpenChange={setConfirmando}
            title={t('perfilCargo.descartarConfirmar.title')}
            // Sin cifra: un peligro del catálogo con medidas propias se queda (KAI-334).
            description={t('perfilCargo.descartarConfirmar.body')}
            porque={{
              label: t('perfilCargo.descartarConfirmar.porqueLabel'),
              testId: 'perfil-cargo-descartar-porque',
              contenidoTestId: 'perfil-cargo-descartar-porque-contenido',
              contenido: <p>{t('perfilCargo.descartarConfirmar.porqueBody', {perfil: fila.origen_catalogo_nombre})}</p>,
            }}
            confirmLabel={t('perfilCargo.descartarConfirmar.confirmar')}
            cancelLabel={t('common.cancel')}
            pending={pendiente}
            onConfirm={() => {
              setConfirmando(false)
              onUsar('descartar')
            }}
          />
        </div>
      )}
    </article>
  )
}
